import { BaseGraphqlLayer } from '@core/base';
import { IMutation, IQuery } from '@introspection/index';
import { Injectable } from '@rxdi/core';
import { from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { SITE_CONTENT_DEFAULTS } from './site-content.defaults';
import { SiteContent, SiteSectionKey, SiteSectionRecord } from './site-content.types';

/** Saved sections over the defaults, field by field (a section saved with fewer fields keeps the rest). */
export function mergeSiteContent(records: SiteSectionRecord[]): SiteContent {
  const content = { ...SITE_CONTENT_DEFAULTS } as SiteContent;
  for (const record of records ?? []) {
    if (record?.key in content && record.data && typeof record.data === 'object') {
      (content as unknown as Record<string, unknown>)[record.key] = {
        ...SITE_CONTENT_DEFAULTS[record.key],
        ...record.data,
      };
    }
  }
  return content;
}

@Injectable()
export class SiteContentService extends BaseGraphqlLayer {
  /** Raw saved sections (admin). */
  sections(fetchPolicy: 'cache-first' | 'network-only' = 'cache-first'): Observable<SiteSectionRecord[]> {
    return this.query<IQuery>({ query: 'site-content.query.graphql', fetchPolicy }).pipe(
      map((res) => (res.data.siteContent ?? []) as unknown as SiteSectionRecord[])
    );
  }

  /** The whole website content: saved sections over the built-in defaults. */
  content(fetchPolicy: 'cache-first' | 'network-only' = 'cache-first'): Observable<SiteContent> {
    return this.sections(fetchPolicy).pipe(map(mergeSiteContent));
  }

  updateSection<K extends SiteSectionKey>(key: K, data: SiteContent[K]): Observable<SiteSectionRecord> {
    return this.mutate<IMutation>({
      mutation: 'update-site-section.mutation.graphql',
      variables: { key, data },
    }).pipe(map((res) => res.data.updateSiteSection as unknown as SiteSectionRecord));
  }

  /** Uploads an image straight to R2 through a presigned URL; resolves to its public URL. */
  uploadImage(file: File): Observable<string> {
    return this.mutate<IMutation>({
      mutation: 'create-upload-url.mutation.graphql',
      variables: { fileName: file.name, contentType: file.type, size: file.size },
    }).pipe(
      switchMap(({ data }) =>
        from(
          fetch(data.createUploadUrl.uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          }).then((res) => {
            if (!res.ok) {
              throw new Error(`Upload failed (${res.status}). Check the R2 bucket CORS rules.`);
            }
            return data.createUploadUrl.publicUrl;
          })
        )
      )
    );
  }
}
